import { Link } from "react-router-dom";

import type { DiscoverDoc } from "@/lib/api";
import { AuthorChip } from "./AuthorChip";
import { TagList } from "./Tag";

/**
 * One row in the question feed. Three small stat boxes on the left
 * (votes / answers / views), title + clamped excerpt in the middle,
 * tags + asker chip at the bottom.
 *
 * Votes + answers are real (denormalized onto the documents row by
 * migration 0010). Views isn't tracked yet — renders `—`.
 */
export function QuestionRow({ doc }: { doc: DiscoverDoc }) {
  return (
    <li className="border-b border-zinc-200 dark:border-zinc-800 last:border-b-0">
      <Link
        to={`/d/${doc.id}`}
        className="grid grid-cols-[auto_minmax(0,1fr)] gap-4 px-2 py-3 hover:bg-zinc-50 dark:hover:bg-zinc-900/30 transition"
      >
        <StatColumn
          votes={doc.question_score}
          answers={doc.answer_count}
        />
        <div className="min-w-0">
          <div className="text-base-dense font-medium text-zinc-900 dark:text-zinc-100 hover:text-accent-fg dark:hover:text-accent truncate">
            {doc.title}
          </div>
          <div className="mt-1 flex items-center justify-between gap-3 flex-wrap">
            <TagList tags={doc.tags} size="xs" />
            <AuthorChip
              displayName={doc.owner.display_name}
              ts={doc.updated_at ?? doc.created_at}
              verb="asked"
            />
          </div>
        </div>
      </Link>
    </li>
  );
}

function StatColumn({ votes, answers }: { votes: number; answers: number }) {
  return (
    <div className="flex flex-col items-end gap-0.5 text-xs-dense text-zinc-500 dark:text-zinc-400 w-20 shrink-0">
      <StatRow value={String(votes)} label={votes === 1 ? "vote" : "votes"} />
      <StatRow
        value={String(answers)}
        label={answers === 1 ? "answer" : "answers"}
      />
      <StatRow value="—" label="views" />
    </div>
  );
}

function StatRow({ value, label }: { value: string; label: string }) {
  return (
    <div className="flex items-baseline gap-1 tabular-nums">
      <span className="font-mono text-zinc-700 dark:text-zinc-300">{value}</span>
      <span>{label}</span>
    </div>
  );
}
